import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { useSettings } from '../context/SettingsContext';
import ResponsiveImg from './ResponsiveImg';
import { socialHref } from '../lib/socials';

const splashHideKey = () => {
  try {
    const host = String(window.location.hostname || '').replace(/^www\./, '').toLowerCase();
    return host ? `bh_splash_hide:${host}` : 'bh_splash_hide';
  } catch {
    return 'bh_splash_hide';
  }
};

const splashSlides = (site) => {
  const raw = Array.isArray(site?.splash_slides) ? site.splash_slides : [];
  const fromSlides = raw.map((s) => {
    if (typeof s === 'string') return { img: s.trim(), img_mobile: '', link: '' };
    const img = String(s?.img || s?.image || '').trim();
    const link = String(s?.link || '').trim();
    return img ? { img, img_mobile: String(s?.img_mobile || '').trim(), link } : null;
  }).filter(Boolean);
  if (fromSlides.length) return fromSlides;
  const imgs = Array.isArray(site?.splash_images)
    ? site.splash_images.map((u) => String(u || '').trim()).filter(Boolean)
    : [];
  if (imgs.length) return imgs.map((img) => ({ img, img_mobile: '', link: '' }));
  const legacy = String(site?.splash_image || '').trim();
  return legacy ? [{ img: legacy, img_mobile: '', link: '' }] : [];
};

const splashFingerprint = (slides, title) => `${slides.map((s) => `${s.img}|${s.img_mobile || ''}`).join(';')}::${title}`;

const Splash = () => {
  const { site } = useSettings();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [idx, setIdx] = useState(0);
  const [hideNext, setHideNext] = useState(false);
  const drag = useRef({ x: 0, on: false });

  const slides = splashSlides(site);
  const enabled = Boolean(site?.splash_enabled) && slides.length > 0;
  const duration = Math.min(15, Math.max(1, Number(site?.splash_duration) || 4));
  const title = String(site?.splash_text || '').trim() || 'Duyuru';
  const n = slides.length;
  const fp = splashFingerprint(slides, title);

  useEffect(() => {
    if (pathname !== '/' || !enabled) {
      setShow(false);
      return;
    }
    let hidden = '';
    try { hidden = localStorage.getItem(splashHideKey()) || ''; } catch { /* ignore */ }
    if (hidden === fp) {
      setShow(false);
      return;
    }
    setIdx(0);
    setHideNext(false);
    setShow(true);
  }, [pathname, enabled, fp]);

  const dismiss = () => {
    if (hideNext) {
      try { localStorage.setItem(splashHideKey(), fp); } catch { /* ignore */ }
    }
    setShow(false);
  };

  const go = (dir) => {
    if (n < 2) return;
    setIdx((i) => (i + dir + n) % n);
  };

  useEffect(() => {
    if (!show || n < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), duration * 1000);
    return () => clearInterval(t);
  }, [show, duration, n]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (hideNext) {
        try { localStorage.setItem(splashHideKey(), fp); } catch { /* ignore */ }
      }
      setShow(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, hideNext, fp]);

  const onPointerDown = (e) => {
    drag.current = { x: e.clientX, on: true };
  };
  const onPointerUp = (e) => {
    if (!drag.current.on) return;
    const dx = e.clientX - drag.current.x;
    drag.current.on = false;
    if (dx < -40) go(1);
    else if (dx > 40) go(-1);
    else {
      const href = socialHref(slides[idx]?.link);
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  if (!show) return null;
  const cur = slides[idx] || slides[0];

  return (
    <div className="home-splash" data-testid="home-splash">
      <div className="home-splash-modal" role="dialog" aria-modal="true" aria-labelledby="home-splash-title">
        <div className="home-splash-head">
          <span id="home-splash-title" className="home-splash-title">{title}</span>
          <button type="button" className="home-splash-x" onClick={dismiss} aria-label="Kapat" data-testid="home-splash-close">
            <X className="w-4 h-4" weight="bold" />
          </button>
        </div>
        <div className="home-splash-body">
          {cur?.img ? (
            <div
              className={`home-splash-stage ${socialHref(cur.link) ? 'is-link' : ''}`}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerCancel={() => { drag.current.on = false; }}
            >
              <ResponsiveImg desktop={cur.img} mobile={cur.img_mobile} alt="" className="home-splash-slide" draggable={false} />
              {n > 1 && (
                <>
                  <button type="button" className="home-splash-nav prev" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Önceki" data-testid="home-splash-prev">
                    <CaretLeft className="w-5 h-5" />
                  </button>
                  <button type="button" className="home-splash-nav next" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Sonraki" data-testid="home-splash-next">
                    <CaretRight className="w-5 h-5" />
                  </button>
                  <div className="home-splash-dots">
                    {slides.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`home-splash-dot-${i}`}
                        className={`home-splash-dot ${i === idx ? 'on' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
        <div className="home-splash-foot">
          <label className="home-splash-hide">
            <input
              type="checkbox"
              checked={hideNext}
              onChange={(e) => setHideNext(e.target.checked)}
              data-testid="home-splash-hide"
            />
            Bu hatırlatmayı bir daha gösterme
          </label>
          <button type="button" className="home-splash-skip" onClick={dismiss} data-testid="home-splash-skip">Kapat</button>
        </div>
      </div>
    </div>
  );
};

export default Splash;
